import strings from 'strings'
import { formatCurrency } from '../BalanceSheet/balanceSheetAdapters'
import { formatPeriodRange, isReported, NOT_REPORTED } from '../CashFlowStatement/cashFlowAdapters'

export { formatCurrency, formatPeriodRange, isReported, NOT_REPORTED }

export const formatOptionalCurrency = (value) => (isReported(value) ? formatCurrency(value) : NOT_REPORTED)

export const formatRatio = (value) => (isReported(value) ? `${Number(value).toFixed(2)}x` : NOT_REPORTED)

const STATUS_LABELS = {
  COMPLETE: strings.DSCR_STATUS_COMPLETE,
  PARTIAL: strings.DSCR_STATUS_PARTIAL,
  NEEDS_VALIDATION: strings.DSCR_STATUS_NEEDS_VALIDATION,
  MISSING_DOCUMENTS: strings.DSCR_STATUS_MISSING_DOCUMENTS,
  MISSING_DATA: strings.DSCR_STATUS_MISSING_DATA,
  INCOMPATIBLE_PERIODS: strings.DSCR_STATUS_INCOMPATIBLE_PERIODS,
  NOT_APPLICABLE: strings.DSCR_STATUS_NOT_APPLICABLE
}

export const statusText = (status) => STATUS_LABELS[status] || status || ''

// Positive: definitive ratio at or above target. Medium: indicative or below target. High: below 1x or nothing computable.
export const statusTone = (result) => {
  if (!result) return 'High'
  if (result.interpretation === 'BELOW_ONE') return 'High'
  if (result.status === 'COMPLETE') return result.interpretation === 'AT_OR_ABOVE_TARGET' ? 'Positive' : 'Medium'
  if (result.status === 'PARTIAL' || result.status === 'NEEDS_VALIDATION') return 'Medium'
  return 'High'
}

export const TONE_COLORS = { Positive: '#2E7D32', Medium: '#ED6C02', High: '#D32F2F' }

export const headlineText = (result) => {
  if (!result) return ''
  switch (result.status) {
    case 'COMPLETE':
      if (result.interpretation === 'BELOW_ONE') return strings.DSCR_HEADLINE_BELOW_ONE
      if (result.interpretation === 'BELOW_TARGET') return strings.DSCR_HEADLINE_BELOW_TARGET
      return strings.DSCR_HEADLINE_COMPLETE
    case 'PARTIAL': return strings.DSCR_HEADLINE_PARTIAL
    case 'NEEDS_VALIDATION': return strings.DSCR_HEADLINE_NEEDS_VALIDATION
    case 'MISSING_DOCUMENTS': return strings.DSCR_HEADLINE_MISSING_DOCUMENTS
    case 'MISSING_DATA': return strings.DSCR_HEADLINE_MISSING_DATA
    case 'INCOMPATIBLE_PERIODS': return strings.DSCR_HEADLINE_INCOMPATIBLE_PERIODS
    case 'NOT_APPLICABLE': return strings.DSCR_HEADLINE_NOT_APPLICABLE
    default: return ''
  }
}

// The figure the banner shows: the definitive ratio, else the indicative one, else nothing.
export const shownRatio = (result) => {
  if (!result) return { value: null, indicative: false }
  if (isReported(result.dscr)) return { value: Number(result.dscr), indicative: false }
  if (isReported(result.indicativeDscr)) return { value: Number(result.indicativeDscr), indicative: true }
  return { value: null, indicative: false }
}

const principalNote = (coverage) => {
  if (coverage === 'COMPLETE') return strings.DSCR_PRINCIPAL_COMPLETE
  if (coverage === 'PARTIAL') return strings.DSCR_PRINCIPAL_PARTIAL
  return strings.DSCR_PRINCIPAL_NOT_FOUND
}

const interestNote = (interest) => {
  if (!interest) return strings.DSCR_INTEREST_MISSING
  if (interest.source === 'INCOME_STATEMENT_ESTIMATE') return strings.DSCR_INTEREST_ESTIMATE
  if (isReported(interest.cashPaid)) return strings.DSCR_INTEREST_CASH_FLOW
  return strings.DSCR_INTEREST_MISSING
}

export const deriveTiles = (result) => {
  if (!result) return []
  const ebitda = result.ebitda || {}
  const principal = result.principalPayments || {}
  const interest = result.interest || {}
  const interestShown = isReported(interest.cashPaid) ? interest.cashPaid
    : interest.source === 'INCOME_STATEMENT_ESTIMATE' ? interest.accruedExpense : null
  return [
    { key: 'ebitda', name: strings.DSCR_KPI_EBITDA, value: formatOptionalCurrency(ebitda.value), sub: ebitda.method === 'REPORTED' ? strings.DSCR_METHOD_REPORTED : ebitda.method === 'DERIVED' ? strings.DSCR_METHOD_DERIVED : '', negative: isReported(ebitda.value) && ebitda.value < 0 },
    { key: 'principal', name: strings.DSCR_KPI_PRINCIPAL, value: formatOptionalCurrency(principal.totalIdentified), sub: principalNote(principal.coverage), negative: false },
    { key: 'interest', name: strings.DSCR_KPI_INTEREST, value: formatOptionalCurrency(interestShown), sub: interestNote(interest), negative: false },
    { key: 'debtService', name: strings.DSCR_KPI_DEBT_SERVICE, value: formatOptionalCurrency(result.totalIdentifiedDebtService), sub: strings.DSCR_DEBT_SERVICE_NOTE, negative: false }
  ]
}

const sourceText = (line) => {
  if (!line) return ''
  const page = line.page ? ` (${strings.CASH_FLOW_PAGE_PREFIX} ${line.page})` : ''
  return `${line.rawLabel || ''}${page}`
}

// One row per component with the document line it came from; the line of credit and unclassified outflows are listed but excluded.
export const deriveComponentRows = (result) => {
  if (!result) return []
  const rows = []
  const ebitda = result.ebitda || {}
  rows.push({ key: 'ebitda', name: strings.DSCR_ROW_EBITDA, source: ebitda.method === 'REPORTED' ? strings.DSCR_METHOD_REPORTED : strings.DSCR_METHOD_DERIVED, value: ebitda.value, excluded: false, note: null })
  const principal = result.principalPayments || {}
  ;(principal.lines || []).forEach((line, i) => rows.push({
    key: `principal-${i}`,
    name: line.normalizedKey === 'finance_lease_payments' ? strings.DSCR_ROW_FINANCE_LEASES : strings.DSCR_ROW_TERM_DEBT,
    source: sourceText(line), value: isReported(line.value) ? Math.abs(line.value) : null, excluded: false, note: null
  }))
  const loc = result.lineOfCredit || {}
  ;(loc.lines || []).forEach((line, i) => rows.push({
    key: `loc-${i}`, name: strings.DSCR_ROW_LOC, source: sourceText(line), value: line.value, excluded: true, note: strings.DSCR_ROW_LOC_NOTE
  }))
  ;(principal.unclassifiedOutflows || []).forEach((line, i) => rows.push({
    key: `unclassified-${i}`, name: strings.DSCR_ROW_UNCLASSIFIED, source: sourceText(line), value: line.value, excluded: true, note: null
  }))
  const interest = result.interest || {}
  if (interest.lines && interest.lines.length > 0) {
    interest.lines.forEach((line, i) => rows.push({ key: `interest-${i}`, name: strings.DSCR_ROW_INTEREST, source: sourceText(line), value: isReported(line.value) ? Math.abs(line.value) : null, excluded: false, note: null }))
  } else if (interest.source === 'INCOME_STATEMENT_ESTIMATE') {
    rows.push({ key: 'interest-estimate', name: strings.DSCR_ROW_INTEREST, source: strings.DSCR_INTEREST_ESTIMATE, value: interest.accruedExpense, excluded: false, note: null })
  }
  if (isReported(interest.accruedExpense) && interest.source !== 'INCOME_STATEMENT_ESTIMATE') {
    rows.push({ key: 'accrued', name: strings.DSCR_ROW_ACCRUED_INTEREST, source: strings.DSCR_METHOD_REPORTED, value: interest.accruedExpense, excluded: true, note: strings.DSCR_ROW_ACCRUED_INTEREST_NOTE })
  }
  return rows
}

export const readResult = (raw) => (raw && typeof raw === 'object' && raw.status ? raw : null)
